import QueryOptions from './QueryOptions';

/**
 * A query service strategy class. It has services to create query strings.
 * @abstract
 */
export default class ODataServiceStrategy {
  /**
   * Ritorna una stringa safe per costruire l'url.
   * @param {string} query query
   */
  // eslint-disable-next-line class-methods-use-this
  safeUrlString(query) {
    if (typeof query !== 'string') return query;
    // rimpiazzo un single quote con due
    // http://docs.oasis-open.org/odata/odata/v4.0/cs01/part2-url-conventions/odata-v4.0-cs01-part2-url-conventions.html
    return encodeURIComponent(query.replace(/'/g, "''"));
  }

  toStringValue(query) {
    return query === null ? 'null' : `'${this.safeUrlString(query)}'`;
  }

  /**
   * Resolve a query item.
   * @public
   */
  resolve = (opt, first, second, func) => {
    let query = '';

    switch (opt) {
      case QueryOptions.EQUAL:
        query = `${first} eq ${second}`;
        break;

      case QueryOptions.NOT_EQUAL:
        query = `${first} ne ${second}`;
        break;

      case QueryOptions.GREATER_THAN:
        query = `${first} gt ${second}`;
        break;

      case QueryOptions.GREATER_THAN_EQUAL:
        query = `${first} ge ${second}`;
        break;

      case QueryOptions.LESS_THAN:
        query = `${first} lt ${second}`;
        break;

      case QueryOptions.LESS_THAN_EQUAL:
        query = `${first} le ${second}`;
        break;

      case QueryOptions.SUBSTRING:
        query = `substringof(${this.toStringValue(second)}, ${this.safeUrlString(first)})`;
        break;

      case QueryOptions.ENDS_WITH:
        query = `endswith(${this.safeUrlString(first)}, ${this.toStringValue(second)})`;
        break;

      case QueryOptions.STARTS_WITH:
        query = `startswith(${this.safeUrlString(first)}, ${this.toStringValue(second)})`;
        break;

      case QueryOptions.RANGE:
        query = second.a ? `${first} le '${second.a}'` : '';
        query = second.a && second.da ? `${query} and ` : query;
        query = second.da ? `${query + first} ge '${second.da}'` : query;
        break;

      case QueryOptions.STRING_EQUAL:
        query = `${first} eq ${this.toStringValue(second)}`;
        break;

      case QueryOptions.STRING_NOT_EQUAL:
        query = `${first} ne ${this.toStringValue(second)}`;
        break;

      case QueryOptions.SUBPROPERTY_EQUAL:
        query = `${first.substring(0, first.indexOf('/'))}/any(a: a/${first.substring(first.indexOf('/') + 1)} eq ${second})`;
        break;

      case QueryOptions.SUBPROPERTY_NOT_EQUAL:
        query = `${first.substring(0, first.indexOf('/'))}/any(a: a/${first.substring(first.indexOf('/') + 1)} ne ${second})`;
        break;

      case QueryOptions.SUBPROPERTY_SUBSTRING:
        query = `${first.substring(0, first.indexOf('/'))}/any(a: substringof(${this.toStringValue(second)}, a/${first.substring(first.indexOf('/') + 1)}))`;
        break;

      case QueryOptions.SUBPROPERTY_ENDS_WITH:
        query = `${first.substring(0, first.indexOf('/'))}/any(a: endswith(a/${first.substring(first.indexOf('/') + 1)},${this.toStringValue(second)}))`;
        break;

      case QueryOptions.SUBPROPERTY_STARTS_WITH:
        query = `${first.substring(0, first.indexOf('/'))}/any(a: startswith(a/${first.substring(first.indexOf('/') + 1)},${this.toStringValue(second)}))`;
        break;

      case QueryOptions.DATE_EQUAL:
        query = `${first} eq ${this.formatDateTime(second)}`;
        break;

      case QueryOptions.DAY_EQUAL: {
        const date = new Date(second);
        query = `day(${first}) eq ${date.getDate()} and month(${first}) eq ${date.getMonth() + 1} and year(${first}) eq ${date.getFullYear()}`;
        break;
      }

      case QueryOptions.DATE_RANGE:
        // eslint-disable-next-line max-len
        query = `${first} ge ${this.formatDateTime(this.formatDate(second.inizio))} and ${first} le ${this.formatDateTime(this.formatDate(second.fine), '23:59:59')}`;
        break;

      case QueryOptions.DATE_GREATER_THAN:
        query = `${first} gt ${this.formatDateTime(second)}`;
        break;

      case QueryOptions.DATE_GREATER_THAN_EQUAL:
        query = `${first} ge ${this.formatDateTime(second)}`;
        break;

      case QueryOptions.DATE_LESS_THAN:
        query = `${first} lt ${this.formatDateTime(second)}`;
        break;

      case QueryOptions.DATE_LESS_THAN_EQUAL:
        query = `${first} le ${this.formatDateTime(second)}`;
        break;

      case QueryOptions.CONTAINS:
        query = `contains(${this.safeUrlString(first)}, ${this.toStringValue(second)})`;
        break;

      case QueryOptions.SUBPROPERTY_CONTAINS:
        query = `${first.substring(0, first.indexOf('/'))}/any(a: contains(a/${first.substring(first.indexOf('/') + 1)},${this.toStringValue(second)}))`;
        break;

      case QueryOptions.CUSTOM_FILTER:
        query = func(first, second);
        break;

      default:
        break;
    }

    return query;
  };

  /**
   * Format to OData DateTime.
   * @param Date is a string of type 'YYYY-MM-DD'.
   * @abstract
   * @protected
   */
  // eslint-disable-next-line class-methods-use-this
  formatDateTime = (date) => { throw Error('Not implemented'); };

  /**
   * Format a date.
   * @protected
   */
  // eslint-disable-next-line class-methods-use-this
  formatDate = (date) => {
    const d = new Date(date);
    return `${d.getFullYear()}-${(`00${d.getMonth() + 1}`).slice(-2)}-${(`00${d.getDate()}`).slice(-2)}`;
  };

  /**
   * Resolve a filter with a set of values.
   * @public
   */
  resolveFilter = (field, values) => {
    const arr = values.map((x) => this.resolve(x.query, field, x.value === false ? x.value : x.value || x.valore, x.customFilter));
    return `(${arr.join(' or ')})`;
  };
}
